import type { Backend } from '@freelancer/datastore/core';
import {
  getQueryParamValue,
  OrderByDirection,
} from '@freelancer/datastore/core';
import { assert, isDefined, isEqual } from '@freelancer/utils';
import type {
  QuotationCancelRequestApi,
  QuotationRejectRequestApi,
  QuotationUpdateRequestApi,
  QuotationVersionUpdateRequestApi,
} from 'api-typings/quotations/quotations';
import {
  QuotationContextTypeApi,
  QuotationStatusApi,
} from 'api-typings/quotations/quotations';
import { SECONDS_IN_A_DAY } from './quotation-revisions.model';
import { transformToDateApi } from './quotation-revisions.transformer';
import type { QuotationRevisionsCollection } from './quotation-revisions.types';

function isTimelineValid({
  startTimestamp,
  endTimestamp,
}: {
  readonly startTimestamp?: number;
  readonly endTimestamp?: number;
}): boolean {
  // We treat the timeline as valid if only start or end date is defined.
  if (!startTimestamp || !endTimestamp) {
    return true;
  }

  const startDate = new Date(startTimestamp);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(endTimestamp);
  endDate.setHours(0, 0, 0, 0);

  return startDate <= endDate;
}

export function quotationRevisionsBackend(): Backend<QuotationRevisionsCollection> {
  return {
    defaultOrder: {
      field: 'id',
      direction: OrderByDirection.ASC,
    },
    fetch: (authUid, versionIds, query, order) => {
      // On the frontend, we are merging quotation & quotation revision
      // into one collection, similar to quotation item & quotation item revision.
      // Any point of time, we should have only one pending or draft quotation revision.
      //
      // The quotations_get endpoint:
      //     - returns only the latest quotation version matching params
      //     - excludes draft unless specified
      // The quotation_revision_get (version history) endpoint:
      //     - returns the given version if specified
      //     - returns all versions if no version was provided
      //
      // Therefore, the endpoint we call depends on what inputs are provided:
      //     - if the statuses/creator_id/context filter is set: quotations_get
      //     - if the quotation version id(s) are set: revision_get
      //     - otherwise, we will fetch the whole revision history using revision_get
      const quotationIds = getQueryParamValue(query, 'quotationId');
      const quotationUids = getQueryParamValue(query, 'quotationUid').map(s =>
        s?.toLowerCase(),
      );
      const statuses = getQueryParamValue(query, 'status');
      const creatorIds = getQueryParamValue(query, 'creatorId');
      const recipientId = getQueryParamValue(query, 'recipientId')[0];
      const contextType = getQueryParamValue(query, 'contextType')[0];
      const contextIds = getQueryParamValue(query, 'contextId');
      const parentTemplateVersionId = getQueryParamValue(
        query,
        'parentTemplateVersionId',
      )[0];

      if (statuses.length > 1) {
        throw new Error('Statuses are mutually exclusive');
      }

      // We have to add a special case condition here for BID type quotes w/ parent template version id
      // Because we won't have the context ID of the bid or the quotation ID
      // So we'll leave it to the backend to find the child quote
      if (
        statuses[0] ||
        creatorIds.length > 0 ||
        recipientId ||
        (contextType && contextIds.length > 0) ||
        (contextType === QuotationContextTypeApi.BID && parentTemplateVersionId)
      ) {
        // Status is set, so we fetch latest matching quotation
        if (
          !!contextType !== contextIds.length > 0 &&
          !(
            contextType === QuotationContextTypeApi.BID &&
            parentTemplateVersionId
          )
        ) {
          throw new Error(
            'Quotation context type and id should be set at the same time',
          );
        }

        return {
          endpoint: `quotations/0.1/quotations`,
          params: {
            ids: quotationIds,
            uids: quotationUids,
            context_type: contextType,
            context_ids: contextIds,
            creator_ids: creatorIds,
            recipient_id: recipientId,
            parent_template_version_id: parentTemplateVersionId,
            statuses,
            ...(recipientId && { recipient_details: true }),
          },
        };
      }

      assert(quotationIds[0] || quotationUids[0], 'Missing quotation id.');
      assert(
        !(quotationIds[0] && quotationUids[0]),
        'Cannot mix ids and uids in query.',
      );

      const quotationVersionUids = getQueryParamValue(query, 'versionUid');

      // Check if version id/uid was provided and fetch the given version
      if (quotationIds[0] && isDefined(versionIds) && versionIds.length > 0) {
        assert(
          quotationIds.length === 1,
          'Cannot provide more than one quotation id when version id is provided.',
        );

        assert(
          quotationVersionUids.length === 0,
          'Cannot mix ids and uids in query.',
        );

        return {
          endpoint: `quotations/0.1/quotations/${quotationIds[0]}/revisions`,
          params: {
            quotation_id: quotationIds[0],
            version_id: versionIds,
          },
        };
      }

      if (quotationUids[0] && quotationVersionUids.length > 0) {
        assert(
          quotationUids.length === 1,
          'Cannot provide more than one quotation uid when version uid is provided.',
        );

        return {
          endpoint: `quotations/0.1/quotations/${quotationUids[0]}/revisions`,
          params: {
            quotation_uid: quotationUids[0],
            version_uid: quotationVersionUids,
          },
        };
      }

      // neither the status or version id is provided
      // check whether id or uid was provided and fetch all versions
      if (quotationIds[0]) {
        // id provided
        assert(
          quotationIds.length === 1,
          'Must provide only one quotation id when fetching all versions',
        );
        return {
          endpoint: `quotations/0.1/quotations/${quotationIds[0]}/revisions`,
          params: {
            quotation_id: quotationIds[0],
          },
        };
      }

      // uid provided
      assert(
        quotationUids.length === 1,
        'Must provide only one quotation uid when fetching all versions',
      );
      return {
        endpoint: `quotations/0.1/quotations/${quotationUids[0]}/revisions`,
        params: {
          quotation_uid: quotationUids[0],
          recipient_details: true,
        },
      };
    },
    push: (_, quotationRevision, extra) => {
      if (quotationRevision.quotationId) {
        // If quotation id is given, we are just creating a new version
        // of a existing quotation
        if (!quotationRevision.parentVersionId) {
          throw new Error(
            'Parent version id is missing creating new quotation version.',
          );
        }

        return {
          endpoint: `quotations/0.1/quotations/${quotationRevision.quotationId}/revisions`,
          payload: {
            quotation_id: quotationRevision.quotationId,
            parent_version_id: quotationRevision.parentVersionId || 0,
            is_requested_by_recipient: quotationRevision.isRequestedByRecipient,
            title: quotationRevision.title,
            description: quotationRevision.description,
            rich_description: quotationRevision.richDescription,
            estimated_start_date: transformToDateApi(
              quotationRevision.estimatedStartDate,
            ),
            estimated_end_date: transformToDateApi(
              quotationRevision.estimatedEndDate,
            ),
            upfront_fund_request_amount:
              quotationRevision.upfrontFundRequestAmount,
          },
          extractId: response => response.quotation.quotation_version_id,
        };
      }

      // If quotaion id 0 (invalid), we are creating a brand new
      // quotation, including the base quotation object, and the
      // first version of the quotation.
      return {
        endpoint: `quotations/0.1/quotations`,
        payload: {
          context_type: quotationRevision.contextType,
          context_id: quotationRevision.contextId,
          currency_id: quotationRevision.defaultCurrencyId,
          parent_template_version_id: quotationRevision.parentTemplateVersionId,
          parent_template_context_id: quotationRevision.parentTemplateContextId,
        },
        extractId: response => response.quotation.id,
      };
    },
    set: undefined,
    update: (authUid, quotationRevision, originalQuotationRevision) => {
      const quotationUri = `quotations/0.1/quotations/${originalQuotationRevision.quotationId}`;

      // The current status of the quotation version we want to update is used to
      // validate update requests.
      const currentStatus = originalQuotationRevision.status;

      // You can only update a quotation revision if it is pending or a draft.
      // If the original quote is a DRAFT, you can update the status (to invalid or pending)
      // as well as the quote details. If it is PENDING, you may only update the status.
      assert(
        currentStatus === QuotationStatusApi.DRAFT ||
          currentStatus === QuotationStatusApi.PENDING,
        'Cannot update a finalised quotation.',
      );

      // Prevent status updates using the status set date properties
      assert(
        !(
          quotationRevision.rejectDate ||
          quotationRevision.cancelDate ||
          quotationRevision.versionInvalidDate
        ),
        'Please update the `status` field instead of setting the status timestamp directly',
      );

      // Validate status transition if a status is provided in the update params
      if (
        quotationRevision.status !== undefined &&
        currentStatus !== quotationRevision.status
      ) {
        // Validate the status field in the quotation version update params
        switch (quotationRevision.status) {
          case undefined:
            // Case when only the quote details need to be updated.
            break;

          case QuotationStatusApi.DRAFT:
            // Draft status is only set on quotation creation and cannot be manually set.
            throw new Error(
              'Cannot manually update a quotation status to draft',
            );

          case QuotationStatusApi.EXPIRED:
            // Quotation expiry status cannot be manually set.
            throw new Error(
              'Cannot manually update a quotation status to expired',
            );

          case QuotationStatusApi.PENDING: {
            // We break if the transition is valid since we allow quotation details to be updated when submitting it.
            assert(
              currentStatus === QuotationStatusApi.DRAFT,
              'Cannot set quotation status to pending if it is not a draft.',
            );
            break;
          }

          case QuotationStatusApi.INVALID: {
            return {
              method: 'PUT',
              endpoint: `${quotationUri}/revisions/${originalQuotationRevision.id}`,
              payload: {
                id: originalQuotationRevision.quotationId,
                quotation_version_id: originalQuotationRevision.id,
                status: quotationRevision.status,
              } as QuotationVersionUpdateRequestApi,
            };
          }

          case QuotationStatusApi.CANCELLED: {
            assert(
              currentStatus === QuotationStatusApi.PENDING ||
                currentStatus === QuotationStatusApi.DRAFT,
              'Cannot cancel a quotation that is not pending.',
            );
            return {
              method: 'POST',
              payload: {
                quotation_id: originalQuotationRevision.quotationId,
              } as QuotationCancelRequestApi,
              endpoint: `${quotationUri}/cancel`,
            };
          }

          case QuotationStatusApi.REJECTED: {
            assert(
              currentStatus === QuotationStatusApi.PENDING,
              'Cannot reject a quotation that is not pending.',
            );
            return {
              method: 'POST',
              payload: {
                quotation_id: originalQuotationRevision.quotationId,
              } as QuotationRejectRequestApi,
              endpoint: `${quotationUri}/reject`,
            };
          }

          case QuotationStatusApi.APPROVED: {
            assert(
              currentStatus === QuotationStatusApi.PENDING,
              'Cannot approve a quotation that is not pending.',
            );

            return {
              method: 'PUT',
              endpoint: `${quotationUri}/revisions/${originalQuotationRevision.id}`,
              payload: {
                id: originalQuotationRevision.quotationId,
                quotation_version_id: originalQuotationRevision.id,
                status: quotationRevision.status,
              } as QuotationVersionUpdateRequestApi,
            };
          }

          default:
            break;
        }
      }

      // Update Quotation context.
      // Quotation details will not be updated if updating the quotation context.
      if (
        quotationRevision.contextType !== undefined ||
        quotationRevision.contextId !== undefined
      ) {
        // Assert that both contextId and contextType are defined simultaneously
        assert(
          quotationRevision.contextId !== undefined &&
            quotationRevision.contextType !== undefined,
          'ContextId and ContextType cannot be updated independently',
        );

        if (
          quotationRevision.contextType !==
            originalQuotationRevision.contextType ||
          quotationRevision.contextId !== originalQuotationRevision.contextId
        ) {
          const payload: QuotationUpdateRequestApi = {
            id: originalQuotationRevision.quotationId,
            context_type: quotationRevision.contextType,
            context_id: quotationRevision.contextId,
          };
          return {
            method: 'PUT',
            payload,
            endpoint: `${quotationUri}`,
          };
        }
      }

      // The details of a quotation revision can only be updated if it is a draft.
      // Or context type is template.
      // If the quote is pending, only its status can be updated, and the function
      // should have returned further up.
      assert(
        currentStatus === QuotationStatusApi.DRAFT ||
          originalQuotationRevision.contextType ===
            QuotationContextTypeApi.TEMPLATE,
        'Only the details of draft quotations can be updated.',
      );

      let payload = {} as QuotationVersionUpdateRequestApi;

      if (
        quotationRevision.status &&
        quotationRevision.status === QuotationStatusApi.PENDING
      ) {
        payload = {
          ...payload,
          status: quotationRevision.status,
        };
      }

      // Ensure that the timeline will not be updated to an invalid state.
      if (
        quotationRevision.estimatedStartDate ||
        quotationRevision.estimatedEndDate
      ) {
        assert(
          isTimelineValid({
            startTimestamp:
              quotationRevision.estimatedStartDate ??
              originalQuotationRevision.estimatedStartDate,
            endTimestamp:
              quotationRevision.estimatedEndDate ??
              originalQuotationRevision.estimatedEndDate,
          }),
          'Quotation end date cannot be before the start date',
        );
      }

      if (
        quotationRevision.estimatedStartDate &&
        !isEqual(
          transformToDateApi(quotationRevision.estimatedStartDate),
          transformToDateApi(originalQuotationRevision.estimatedStartDate),
        )
      ) {
        payload = {
          ...payload,
          estimated_start_date: transformToDateApi(
            quotationRevision.estimatedStartDate,
          ),
        };
      }
      if (
        quotationRevision.estimatedEndDate &&
        !isEqual(
          transformToDateApi(quotationRevision.estimatedEndDate),
          transformToDateApi(originalQuotationRevision.estimatedEndDate),
        )
      ) {
        payload = {
          ...payload,
          estimated_end_date: transformToDateApi(
            quotationRevision.estimatedEndDate,
          ),
        };
      }

      if (
        quotationRevision.description &&
        quotationRevision.description.trim() !==
          originalQuotationRevision.description
      ) {
        payload = {
          ...payload,
          description: quotationRevision.description.trim(),
        };
      }

      if (
        quotationRevision.richDescription &&
        quotationRevision.richDescription.trim() !==
          originalQuotationRevision.richDescription
      ) {
        payload = {
          ...payload,
          rich_description: quotationRevision.richDescription.trim(),
        };
      }

      if (
        quotationRevision.title &&
        quotationRevision.title.trim() !== originalQuotationRevision.title
      ) {
        payload = {
          ...payload,
          title: quotationRevision.title.trim(),
        };
      }

      if (
        quotationRevision.upfrontFundRequestAmount !== undefined &&
        quotationRevision.upfrontFundRequestAmount !==
          originalQuotationRevision.upfrontFundRequestAmount
      ) {
        payload = {
          ...payload,
          upfront_fund_request_amount:
            quotationRevision.upfrontFundRequestAmount,
        };
      }

      if (
        quotationRevision.durationInDays !== undefined &&
        quotationRevision.durationInDays !==
          originalQuotationRevision.durationInDays
      ) {
        payload = {
          ...payload,
          // As duration is worked with in days on the frontend, but
          // handled as seconds in the api and db, we need to convert the
          // days to seconds by multiplying by seconds in a day
          duration: quotationRevision.durationInDays * SECONDS_IN_A_DAY,
        };
      }

      if (
        quotationRevision.expiryDate !== undefined &&
        quotationRevision.expiryDate !== originalQuotationRevision.expiryDate
      ) {
        // Ensure the time is the end of the day
        const dayOfExpiry = new Date(quotationRevision.expiryDate);
        const endOfExpiryDayTimestamp =
          dayOfExpiry.setHours(23, 59, 59, 0) / 1000;
        payload = {
          ...payload,
          expiry_date: endOfExpiryDayTimestamp,
        };
      }

      if (quotationRevision.logoId !== undefined) {
        payload = {
          ...payload,
          logo_id: quotationRevision.logoId,
        };
      }

      if (
        quotationRevision.defaultCurrencyId !== undefined &&
        quotationRevision.defaultCurrencyId !==
          originalQuotationRevision.defaultCurrencyId
      ) {
        payload = {
          ...payload,
          currency_id: quotationRevision.defaultCurrencyId,
        };
      }

      payload = {
        ...payload,
        id: originalQuotationRevision.quotationId,
        quotation_version_id: originalQuotationRevision.id,
      };

      return {
        method: 'PUT',
        endpoint: `quotations/0.1/quotations/${payload.id}/revisions/${payload.quotation_version_id}`,
        payload,
      };
    },
    remove: undefined,
  };
}
